<template>
  <BottomDrawer
    :isOpen="isDetailsDrawerOpen"
    @close="closeDetailsDrawer"
  >
    <template #header>
      <div class="drawer-trip-header">
        <div class="drawer-trip-header__stations">
          {{ selectedRoute?.legs[0].departure_station.name }} -
          {{
            selectedRoute?.legs[selectedRoute?.legs.length - 1].arrival_station
              .name
          }}
        </div>
        <div class="drawer-trip-header__time">
          {{ formatDateWithTime(
            selectedRoute?.legs[0].service_schedule_date ?? '',
            selectedRoute?.legs[0].departure_station.departure_timestamp ?? '',
            selectedRoute?.legs[selectedRoute?.legs.length - 1].arrival_station.arrival_timestamp ?? ''
          ) }}
        </div>
      </div>
    </template>
    <template v-if="!isTicketsDrawerOpen">
      <JourneyListDetails
        :legs="
          selectedRoute?.legs?.map((leg) => ({
            ...leg,
            departure_station: {
              ...leg.departure_station,
              departure_timestamp:
                leg.departure_station.departure_timestamp || '',
            },
            arrival_station: {
              ...leg.arrival_station,
              arrival_timestamp: leg.arrival_station.arrival_timestamp || '',
            },
          })) || []
        "
        :travelDate="
          selectedRoute?.legs?.[0]?.service_schedule_date?.split('T')[0] || ''
        "
        :serviceName="selectedRoute?.legs?.[0]?.service_name || ''"
        :departureStation="
          selectedRoute?.legs?.[0]?.departure_station.name || ''
        "
        :arrivalStation="
          selectedRoute?.legs?.[selectedRoute?.legs.length - 1]?.arrival_station
            .name || ''
        "
        @update-route-codes="
          updateRouteCodes(selectedTravelIndex, selectedRouteIndex, $event)
        "
      />
    </template>
    <template #footer>
      <div class="drawer-buttons">
        <button
          class="button button--medium button--secondary"
          @click="closeDetailsDrawer"
        >
          {{ $t('common.actions.cancel') }}
        </button>
        <button
          class="button button--primary button--medium drawer-buttons__btn"
          @click="openTicketsDrawer"
        >
          {{ $t('travelPlanner.journeyList.tickets.title') }}
        </button>
      </div>
    </template>
  </BottomDrawer>

  <BottomDrawer
    :isOpen="isTicketsDrawerOpen"
    @close="closeTicketsDrawer"
  >
    <template #header>
      <div class="drawer-trip-header">
        <div class="drawer-trip-header__stations">
          {{ selectedRoute?.legs[0].departure_station.name }} -
          {{
            selectedRoute?.legs[selectedRoute?.legs.length - 1].arrival_station
              .name
          }}
        </div>
        <div class="drawer-trip-header__time">
          {{ formatDateWithTime(
            selectedRoute?.legs[0].service_schedule_date ?? '',
            selectedRoute?.legs[0].departure_station.departure_timestamp ?? '',
            selectedRoute?.legs[selectedRoute?.legs.length - 1].arrival_station.arrival_timestamp ?? ''
          ) }}
        </div>
      </div>
    </template>
    <JourneyListTickets
      :bundles="selectedRoute?.bundles || []"
      :travelIndex="selectedTravelIndex"
      :routeIndex="selectedRouteIndex"
      :showOutbound="isOutbound"
      :legs="selectedRoute?.legs || []"
      @bundle-selected="handleBundleSelection"
    />
    <template #footer>
      <div class="drawer-buttons">
        <button
          class="button button--medium button--secondary"
          @click="closeTicketsDrawer"
        >
          Avbryt
        </button>
        <RebookingHandler
          v-if="isRebooking"
          :selectedBundle="getSelectedBundle()"
          :selectedTravel="selectedTravel"
          :selectedReturnTravel="selectedReturnTravel"
          :returnTravels="returnTravels || []"
          :showInboundTravels="handleShowInboundTravels"
          :bookingNumber="bookingStore.bookingData?.booking_number || ''"
          :itemsToReplace="itemsToReplace"
          class="drawer-buttons__btn"
        />
        <BookingHandler
          v-else-if="getSelectedBundle()"
          :selectedBundle="getSelectedBundle()"
          :selectedTravel="selectedTravel"
          :isOutbound="isOutbound"
          :returnTravels="returnTravels || []"
          :selectedReturnTravel="selectedReturnTravel"
          :showInboundTravels="handleShowInboundTravels"
          class="drawer-buttons__btn"
        />
      </div>
    </template>
  </BottomDrawer>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import { Travel, Route } from '@/types/travelTypes'
  import { SearchSelection } from '@/types/searchTypes'
  import { Bundle } from '@/types/journeyTypes'
  import JourneyListDetails from './JourneyListDetails.vue'
  import JourneyListTickets from './JourneyListTickets.vue'
  import BottomDrawer from '@/components/BaseComponents/BottomDrawer.vue'
  import BookingHandler from '@/components/TravelPlanner/BookingHandler.vue'
  import RebookingHandler from '@/components/TravelPlanner/RebookingHandler.vue'
  import { useBookingStore } from '@/stores/bookingStore'
  import { formatDate, formatDateWithTime } from '@/utils/dateFormatter'
  import { formatTime } from '@/utils/dateUtils'

  export default defineComponent({
    name: 'JourneyListMobile',
    components: {
      JourneyListDetails,
      JourneyListTickets,
      BottomDrawer,
      BookingHandler,
      RebookingHandler,
    },
    props: {
      selectedRoute: {
        type: Object as PropType<Route | null>,
        default: null,
      },
      selectedTravel: {
        type: Object as PropType<Travel | null>,
        default: null,
      },
      selectedBundle: {
        type: [Object, null] as PropType<{ bundleIndex: number } | null>,
        default: null,
      },
      selectedTravelIndex: {
        type: Number,
        required: true,
      },
      selectedRouteIndex: {
        type: Number,
        required: true,
      },
      isDetailsDrawerOpen: {
        type: Boolean,
        required: true,
      },
      isTicketsDrawerOpen: {
        type: Boolean,
        required: true,
      },
      isOutbound: {
        type: Boolean,
        required: true,
      },
      isRebooking: {
        type: Boolean,
        default: false,
      },
      itemsToReplace: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      returnTravels: {
        type: Array as PropType<Travel[]>,
        default: () => [],
      },
      selectedReturnTravel: {
        type: Object as PropType<Travel | null>,
        default: null,
      },
      showInboundTravels: {
        type: Function as PropType<() => void>,
        required: true,
      },
    },
    emits: [
      'update-route-codes',
      'bundle-selected',
      'close-details',
      'close-tickets',
      'open-tickets',
      'show-inbound-travels',
    ],
    setup(props, { emit }) {
      const bookingStore = useBookingStore()

      const getSelectedBundle = (): Bundle | null => {
        if (
          !props.selectedBundle ||
          !props.selectedTravel ||
          !props.selectedRoute ||
          typeof props.selectedBundle.bundleIndex !== 'number'
        ) {
          return null
        }

        const bundle =
          props.selectedRoute.bundles?.[props.selectedBundle.bundleIndex]
        return bundle || null
      }

      const handleBundleSelection = (selection: SearchSelection) => {
        emit('bundle-selected', selection)
      }

      const closeDetailsDrawer = () => {
        emit('close-details')
      }

      const closeTicketsDrawer = () => {
        emit('close-tickets')
      }

      const openTicketsDrawer = () => {
        closeDetailsDrawer()
        emit('open-tickets')
      }

      const handleShowInboundTravels = () => {
        closeTicketsDrawer()
        emit('show-inbound-travels')
      }

      const updateRouteCodes = (
        travelIndex: number,
        routeIndex: number,
        routeCodes: string[]
      ) => {
        emit('update-route-codes', { travelIndex, routeIndex, routeCodes })
      }

      return {
        getSelectedBundle,
        handleBundleSelection,
        closeDetailsDrawer,
        closeTicketsDrawer,
        openTicketsDrawer,
        updateRouteCodes,
        handleShowInboundTravels,
        formatDate,
        formatTime,
        formatDateWithTime,
        bookingStore,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .drawer-buttons {
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    gap: 1rem;
    margin: 0 -1rem;
    padding: 1rem 3rem;
    border-top: 1px solid #ddd;
    background: white;

    @media (max-width: 440px) {
      &__btn {
        flex-basis: 100%;
        max-width: 100%;
      }
    }
  }

  .drawer-trip-header {
    margin: 0 -1rem;
    padding: 1.25rem 3rem;
    
    &__stations {
      @include title-medium;
      margin-bottom: 0.5rem;
      color: $clr-primary;
    }

    &__time {
      @include body-medium;
      color: $clr-text;
    }
  }
</style>
